import type {
  FormularyResponseT,
  PayerInformationT,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import { Loader2, Pill } from 'lucide-react';

import type { RxMedication } from '@/entities/rx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { cn } from '@/lib/utils';

import { deriveFormularyDisplayState } from '../model/formularyDisplayPolicy';

import {
  PayerAlternativesSection,
  TherapeuticAlternativesSection,
} from './BenefitsCoordinationPanel';

export interface AlternativesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formulary: FormularyResponseT | null | undefined;
  payerInformation: PayerInformationT | null | undefined;
  /** The current medication — used by the policy filter (multi-ingredient, obsolete). */
  medication: RxMedication | null | undefined;
  /** Display name for the currently selected medication (shown in header). */
  currentMedicationName: string;
  /** Loading flag — show a spinner if formulary is still being fetched. */
  isLoading: boolean;
  /**
   * Callback fired when the clinician picks an alternative. Receives the
   * DoseSpot ``DispensableDrugId``. The dialog closes itself after the
   * call returns successfully (regardless of resolved value).
   */
  onSelectAlternative: ((dispensableDrugId: number) => Promise<void> | void) | undefined;
  /** True while a swap is in flight; disables click-to-select buttons. */
  isSelectingAlternative: boolean;
}

const TITLE_ID = 'alternatives-dialog-title';
const DESCRIPTION_ID = 'alternatives-dialog-description';

/**
 * Modal launched from the Details step when a clinician wants to swap
 * the currently-selected medication for a payer-preferred or
 * therapeutic alternative without going back to the search.
 *
 * Reuses the same alternative sections rendered in
 * :class:`BenefitsCoordinationPanel` on the Review step — single source
 * of truth for status / copay / "Use this" affordances.
 *
 * Per ACR PA.109.1 and the F1–F7 cert matrix: therapeutic alternatives
 * must NOT be shown when the plan provides a payer ALT file; alts
 * cannot be displayed at all when the target is UNKNOWN or OBSOLETE.
 * Both rules live in :mod:`formularyDisplayPolicy` and are applied
 * here via :func:`deriveFormularyDisplayState`.
 */
export function AlternativesDialog({
  open,
  onOpenChange,
  formulary,
  payerInformation,
  medication,
  currentMedicationName,
  isLoading,
  onSelectAlternative,
  isSelectingAlternative,
}: AlternativesDialogProps) {
  const display = deriveFormularyDisplayState({ formulary, medication });
  const planLabel = payerInformation?.PlanName ?? payerInformation?.PayerName ?? null;
  const hasAnyAlternatives =
    display.payerAlternatives.length > 0 || display.therapeuticAlternatives.length > 0;

  // Auto-close on a successful swap. The switcher hook updates the
  // form synchronously after ``select_medication`` resolves — wrapping
  // the parent callback here keeps DetailsStep ignorant of dialog
  // state. ``isSelectingAlternative`` is still surfaced down to the
  // rows so they can disable while in-flight.
  const handleSelect = onSelectAlternative
    ? async (dispensableDrugId: number) => {
        await onSelectAlternative(dispensableDrugId);
        onOpenChange(false);
      }
    : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-labelledby={TITLE_ID}
        aria-describedby={DESCRIPTION_ID}
        className="sm:max-w-2xl"
      >
        <DialogHeader>
          <div className="flex items-start gap-2.5">
            <div
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-full',
                'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
              )}
            >
              <Pill className="size-5" aria-hidden />
            </div>
            <div className="min-w-0 space-y-1.5">
              <DialogTitle id={TITLE_ID}>Medication alternatives</DialogTitle>
              <DialogDescription id={DESCRIPTION_ID}>
                Currently selected:{' '}
                <span className="font-medium text-foreground">{currentMedicationName}</span>
                {planLabel ? (
                  <>
                    {' '}
                    on <span className="font-medium text-foreground">{planLabel}</span>
                  </>
                ) : null}
                . Pick an alternative below to swap it into the prescription.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          {isLoading ? (
            <div className="flex items-center gap-2 rounded-md border border-border/40 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
              Loading formulary alternatives…
            </div>
          ) : null}

          {!isLoading && display.policy.notice ? (
            <div className="rounded-md border border-border/40 bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
              {display.policy.notice}
            </div>
          ) : null}

          {!isLoading && display.payerAlternatives.length > 0 ? (
            <PayerAlternativesSection
              alternatives={display.payerAlternatives}
              onSelectAlternative={handleSelect}
              isSelectingAlternative={isSelectingAlternative}
            />
          ) : null}

          {!isLoading && display.therapeuticAlternatives.length > 0 ? (
            <TherapeuticAlternativesSection
              alternatives={display.therapeuticAlternatives}
              onSelectAlternative={handleSelect}
              isSelectingAlternative={isSelectingAlternative}
            />
          ) : null}

          {!isLoading && !hasAnyAlternatives && !display.policy.notice ? (
            <div className="rounded-md border border-border/40 bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
              No alternatives are available for this medication on the patient's plan.
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
